import {
  NotificationTypes,
  notificationTypesReadable,
} from '@dynamika-shared/types/subscription';
import { TeamMember } from '@dynamika-shared/types/teamMember';
import { UserRoles } from '@dynamika-shared/types/auth';
import { RecipientContent } from '@api/subscriptions';
import { rolesMap } from 'src/constants/rolesList';
import { NotificationRecipientType } from '../constants';
import { SiteRecipientRow } from './siteRecipients.types';

const EXTERNAL_USER_ROLE_LABEL = 'External User';

// Canonical order, so a row's tags read the same way whatever order the
// backend lists a recipient's events in.
const EVENT_ORDER = Object.values(NotificationTypes);

/**
 * `userId` is absent for external (EMAIL) recipients, so the email is the
 * fallback key — lower-cased, since the backend does not normalise case and two
 * casings of one address must not read as two people.
 */
const getRecipientKey = (recipient: RecipientContent): string =>
  recipient.userId || recipient.channelIdentifier?.trim().toLowerCase() || '';

const resolveRole = (
  recipient: RecipientContent,
  teamMembersById: Map<string, TeamMember>
): string => {
  if (recipient.recipientType === NotificationRecipientType.EMAIL) {
    return EXTERNAL_USER_ROLE_LABEL;
  }

  const role =
    recipient.recipientRoles ||
    teamMembersById.get(recipient.userId ?? '')?.roles;

  if (!role) return '-';

  return rolesMap[role as UserRoles]?.label ?? role;
};

export type BuildSiteRecipientRowsArgs = {
  recipients: RecipientContent[];
  teamMembers: TeamMember[];
};

/**
 * Reshape the recipients payloads into table rows, filling in the role.
 */
export const buildSiteRecipientRows = ({
  recipients,
  teamMembers,
}: BuildSiteRecipientRowsArgs): SiteRecipientRow[] => {
  const teamMembersById = new Map(
    (teamMembers ?? []).map(member => [member.id, member])
  );

  const rowsByKey = new Map<string, SiteRecipientRow>();
  const eventsByKey = new Map<string, Set<NotificationTypes>>();

  (recipients ?? []).forEach(recipient => {
    const key = getRecipientKey(recipient);

    if (!key) return;

    const events = eventsByKey.get(key) ?? new Set<NotificationTypes>();

    (recipient.notificationTypes ?? []).forEach(event => events.add(event));
    eventsByKey.set(key, events);

    if (!rowsByKey.has(key)) {
      rowsByKey.set(key, {
        key,
        fullName: recipient.fullName ?? '',
        email: recipient.channelIdentifier ?? '',
        recipientType:
          (recipient.recipientType as NotificationRecipientType) ??
          NotificationRecipientType.USER,
        role: resolveRole(recipient, teamMembersById),
        events: [],
      });
    }
  });

  return [...rowsByKey.values()].map(row => ({
    ...row,
    events: EVENT_ORDER.filter(event => eventsByKey.get(row.key)?.has(event)),
  }));
};

const getSortValue = (row: SiteRecipientRow, column: string): string => {
  switch (column) {
    case 'recipientType':
      return row.recipientType;
    case 'role':
      return row.role;
    case 'events':
      return row.events.map(event => notificationTypesReadable[event]).join(', ');
    case 'fullName':
    default:
      return row.fullName || row.email;
  }
};

/**
 * Sort locally from the `"<column>,<direction>"` state the shared `Table`
 * emits — the sibling tabs send it as a query param, here it never leaves the
 * browser. An unrecognised column falls back to Recipient ascending.
 */
export const sortSiteRecipientRows = (
  rows: SiteRecipientRow[],
  sortState?: string
): SiteRecipientRow[] => {
  const [column = 'fullName', direction = 'asc'] = (
    sortState ?? 'fullName,asc'
  ).split(',');

  const factor = direction === 'desc' ? -1 : 1;

  return [...rows].sort(
    (a, b) =>
      factor * getSortValue(a, column).localeCompare(getSortValue(b, column))
  );
};

/** Slice the rows for the current page — the list is never paged server-side. */
export const paginateSiteRecipientRows = (
  rows: SiteRecipientRow[],
  currentPage: number,
  pageSize: number
): SiteRecipientRow[] => {
  const start = (currentPage - 1) * pageSize;

  return rows.slice(start, start + pageSize);
};
